import React from 'react';
import { Switch } from 'evergreen-ui';
import {
    Desc,
    ForthStepWrapper,
    ScanSettingSection,
    ScanSettingWrapper,
    Title,
    TitleWrapper,
} from '../ForthStep/style';
import {
    ButtonWrapper,
    PrevButton,
    ScanSetupFooterWrapper,
    ScanSetupHeaderTitle,
    ScanSetupHeaderTitleWrapper,
} from '../../../style';
import AppButton from '../../../../../components/AppButton/AppButton';
import ScanStepsFlow from '../../ScanStepsFlow';
import { KillerQuestionsEditor } from 'components/KillerQuestionsEditor/KillerQuestionsEditor';
import {
    isValidMeetingUrl,
    SUPPORTED_MEETING_PLATFORMS_LABEL,
} from 'utils/meetingDomains';
import { CreateScanMotiviewStepProps } from '../../../../../typings';

/**
 * Everything specific to a Motiview group, on a step of its own ahead of the
 * review. Only reached for Motiview groups whose user has access.
 */
const MotiviewStep = (props: CreateScanMotiviewStepProps) => {
    const {
        goToNextStep,
        goToPrevStep,
        newAssessmentSection,
        isScanSetupWizard,
        allowMotiviewRegistration,
        setAllowMotiviewRegistration,
        isWebsiteDemo,
        setIsWebsiteDemo,
        allowRepeatMotiviewScan,
        setAllowRepeatMotiviewScan,
        showMotiviewDetailsFormBeforeResult,
        setShowMotiviewDetailsFormBeforeResult,
        showMotiviewDetailsFormAfterResult,
        setShowMotiviewDetailsFormAfterResult,
        allowMeetingAboveAverage,
        setAllowMeetingAboveAverage,
        allowMeetingAverage,
        setAllowMeetingAverage,
        allowMeetingBelowAverage,
        setAllowMeetingBelowAverage,
        meetingUrls,
        setMeetingUrls,
        showCompanyLogo,
        setShowCompanyLogo,
        sendPdfReport,
        setSendPdfReport,
        killerQuestionsEnabled,
        setKillerQuestionsEnabled,
        killerQuestions,
        setKillerQuestions,
        killerQuestionsMinCorrect,
        setKillerQuestionsMinCorrect,
        killerQuestionsRedirectUrl,
        setKillerQuestionsRedirectUrl,
        showKillerQuestionErrors,
        killerQuestionsFormError,
        showMeetingError,
    } = props;

    const anyMeetingFlag =
        !!allowMeetingAboveAverage ||
        !!allowMeetingAverage ||
        !!allowMeetingBelowAverage;

    return (
        <>
            <ScanSetupHeaderTitleWrapper>
                <ScanSetupHeaderTitle>Motiview setup</ScanSetupHeaderTitle>
            </ScanSetupHeaderTitleWrapper>
            <ScanStepsFlow
                isScanSetupWizard={isScanSetupWizard}
                active={['first', 'second', 'third', 'forth']}
                existingAssessmentSection={
                    isScanSetupWizard ? !newAssessmentSection : undefined
                }
                motiviewStep
            />
            <ForthStepWrapper>
                <ScanSettingSection>
                    <Title>Motiview Settings</Title>
                    <ScanSettingWrapper className="with-sub-settings">
                        <TitleWrapper>
                            <Title className="small">
                                Allow Motiview Registration
                            </Title>
                        </TitleWrapper>
                        <div className="switch-wrapper">
                            <Switch
                                height={24}
                                checked={allowMotiviewRegistration}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ) =>
                                    setAllowMotiviewRegistration(
                                        e.target.checked,
                                    )
                                }
                                className="switch"
                            />
                        </div>
                    </ScanSettingWrapper>
                    <ScanSettingWrapper className="sub-settings">
                        <TitleWrapper>
                            <Desc>
                                Enable this option to allow participants to
                                register for Motiview assessments
                            </Desc>
                        </TitleWrapper>
                    </ScanSettingWrapper>
                    <hr className="line"></hr>
                    <ScanSettingWrapper className="with-sub-settings">
                        <TitleWrapper>
                            <Title className="small">
                                Activate Website Version
                            </Title>
                        </TitleWrapper>
                        <div className="switch-wrapper">
                            <Switch
                                height={24}
                                checked={isWebsiteDemo}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ) => setIsWebsiteDemo?.(e.target.checked)}
                                className="switch"
                            />
                        </div>
                    </ScanSettingWrapper>
                    <ScanSettingWrapper className="sub-settings">
                        <TitleWrapper>
                            <Desc>
                                Enable this option if you want to use the
                                website version of Motiview
                            </Desc>
                        </TitleWrapper>
                    </ScanSettingWrapper>
                    <hr className="line"></hr>
                    <ScanSettingWrapper className="with-sub-settings">
                        <TitleWrapper>
                            <Title className="small">Allow Repeat Scan</Title>
                        </TitleWrapper>
                        <div className="switch-wrapper">
                            <Switch
                                height={24}
                                checked={allowRepeatMotiviewScan}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ) =>
                                    setAllowRepeatMotiviewScan?.(
                                        e.target.checked,
                                    )
                                }
                                className="switch"
                            />
                        </div>
                    </ScanSettingWrapper>
                    <ScanSettingWrapper className="sub-settings">
                        <TitleWrapper>
                            <Desc>
                                Allow candidates to complete the Motiview scan
                                multiple times from the same device
                            </Desc>
                        </TitleWrapper>
                    </ScanSettingWrapper>
                    <hr className="line"></hr>
                    <ScanSettingWrapper>
                        <TitleWrapper>
                            <Title className="small">
                                Show Candidate Details Form
                            </Title>
                            <Desc>
                                Collect the candidate's details via an intake
                                form. Choose to display it before the slideshow
                                during the analysis wait, or after the result on
                                the way to scheduling. Only one option can be
                                enabled at a time.
                            </Desc>
                        </TitleWrapper>
                        <div
                            className="switch-wrapper"
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12,
                                alignItems: 'flex-end',
                            }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                }}>
                                <span
                                    style={{
                                        fontSize: '0.9em',
                                        color: '#445870',
                                    }}>
                                    Before result
                                </span>
                                <Switch
                                    height={24}
                                    checked={
                                        showMotiviewDetailsFormBeforeResult
                                    }
                                    disabled={
                                        showMotiviewDetailsFormAfterResult
                                    }
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        // Mutually exclusive with "After result" —
                                        // turning this on forces the other off,
                                        // and each is disabled while the other is on.
                                        setShowMotiviewDetailsFormBeforeResult?.(
                                            e.target.checked,
                                        );
                                        if (e.target.checked)
                                            setShowMotiviewDetailsFormAfterResult?.(
                                                false,
                                            );
                                    }}
                                    className="switch"
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                }}>
                                <span
                                    style={{
                                        fontSize: '0.9em',
                                        color: '#445870',
                                    }}>
                                    After result
                                </span>
                                <Switch
                                    height={24}
                                    checked={showMotiviewDetailsFormAfterResult}
                                    disabled={
                                        showMotiviewDetailsFormBeforeResult
                                    }
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        setShowMotiviewDetailsFormAfterResult?.(
                                            e.target.checked,
                                        );
                                        if (e.target.checked)
                                            setShowMotiviewDetailsFormBeforeResult?.(
                                                false,
                                            );
                                    }}
                                    className="switch"
                                />
                            </div>
                        </div>
                    </ScanSettingWrapper>
                    <hr className="line"></hr>
                    <ScanSettingWrapper className="with-sub-settings">
                        <TitleWrapper>
                            <Title className="small">Show Company Logo</Title>
                        </TitleWrapper>
                        <div className="switch-wrapper">
                            <Switch
                                height={24}
                                checked={showCompanyLogo}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ) => setShowCompanyLogo?.(e.target.checked)}
                                className="switch"
                            />
                        </div>
                    </ScanSettingWrapper>
                    <ScanSettingWrapper className="sub-settings">
                        <TitleWrapper>
                            <Desc>
                                Display the company logo instead of the Motiview
                                logo
                            </Desc>
                        </TitleWrapper>
                    </ScanSettingWrapper>
                    <hr className="line"></hr>
                    <ScanSettingWrapper className="with-sub-settings">
                        <TitleWrapper>
                            <Title className="small">
                                Allow Report by Email
                            </Title>
                        </TitleWrapper>
                        <div className="switch-wrapper">
                            <Switch
                                height={24}
                                checked={sendPdfReport}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ) => setSendPdfReport?.(e.target.checked)}
                                className="switch"
                            />
                        </div>
                    </ScanSettingWrapper>
                    <ScanSettingWrapper className="sub-settings">
                        <TitleWrapper>
                            <Desc>
                                Allow candidates to email themselves the PDF
                                report. A signed link valid for 14 days is sent
                                to the address they enter, with a maximum of 3
                                sends per candidate.
                            </Desc>
                        </TitleWrapper>
                    </ScanSettingWrapper>
                    <hr className="line"></hr>
                    <ScanSettingWrapper className="with-sub-settings">
                        <TitleWrapper>
                            <Title className="small">Killer Questions</Title>
                        </TitleWrapper>
                        <div className="switch-wrapper">
                            <Switch
                                height={24}
                                checked={killerQuestionsEnabled}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>,
                                ) =>
                                    setKillerQuestionsEnabled?.(
                                        e.target.checked,
                                    )
                                }
                                className="switch"
                            />
                        </div>
                    </ScanSettingWrapper>
                    <ScanSettingWrapper className="sub-settings">
                        <TitleWrapper>
                            <Desc>
                                Ask up to 5 questions before the video.
                                Candidates who do not pass are sent to a web
                                address of your choosing and are not registered.
                                These questions cannot be changed after the
                                group is created.
                            </Desc>
                        </TitleWrapper>
                    </ScanSettingWrapper>
                    {killerQuestionsEnabled && (
                        <ScanSettingWrapper className="sub-settings">
                            <KillerQuestionsEditor
                                questions={killerQuestions ?? []}
                                onQuestionsChange={(next) =>
                                    setKillerQuestions?.(next)
                                }
                                minCorrect={killerQuestionsMinCorrect ?? 0}
                                onMinCorrectChange={(next) =>
                                    setKillerQuestionsMinCorrect?.(next)
                                }
                                redirectUrl={killerQuestionsRedirectUrl ?? ''}
                                onRedirectUrlChange={(next) =>
                                    setKillerQuestionsRedirectUrl?.(next)
                                }
                                showErrors={showKillerQuestionErrors}
                            />
                        </ScanSettingWrapper>
                    )}
                    {killerQuestionsEnabled &&
                        showKillerQuestionErrors &&
                        killerQuestionsFormError && (
                            <ScanSettingWrapper className="sub-settings">
                                <Desc style={{ color: '#FF5756' }}>
                                    {killerQuestionsFormError}
                                </Desc>
                            </ScanSettingWrapper>
                        )}
                    <hr className="line"></hr>
                    <ScanSettingWrapper>
                        <TitleWrapper>
                            <Title className="small">Allow Meetings</Title>
                            <Desc>
                                Show meeting-scheduling URLs on the result page
                                based on the candidate's culture-fit score. Each
                                score level can be enabled independently. At
                                least one Meeting URL must be configured for any
                                enabled level to take effect.
                            </Desc>
                        </TitleWrapper>
                        <div
                            className="switch-wrapper"
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 12,
                                alignItems: 'flex-end',
                            }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                }}>
                                <span
                                    style={{
                                        fontSize: '0.9em',
                                        color: '#445870',
                                    }}>
                                    Above average
                                </span>
                                <Switch
                                    height={24}
                                    checked={allowMeetingAboveAverage}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setAllowMeetingAboveAverage?.(
                                            e.target.checked,
                                        )
                                    }
                                    className="switch"
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                }}>
                                <span
                                    style={{
                                        fontSize: '0.9em',
                                        color: '#445870',
                                    }}>
                                    Average
                                </span>
                                <Switch
                                    height={24}
                                    checked={allowMeetingAverage}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setAllowMeetingAverage?.(
                                            e.target.checked,
                                        )
                                    }
                                    className="switch"
                                />
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                }}>
                                <span
                                    style={{
                                        fontSize: '0.9em',
                                        color: '#445870',
                                    }}>
                                    Below average
                                </span>
                                <Switch
                                    height={24}
                                    checked={allowMeetingBelowAverage}
                                    onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setAllowMeetingBelowAverage?.(
                                            e.target.checked,
                                        )
                                    }
                                    className="switch"
                                />
                            </div>
                        </div>
                    </ScanSettingWrapper>
                    {anyMeetingFlag && (
                        <ScanSettingWrapper className="sub-settings">
                            {/* Header: title + description on the left, "+ Add URL" on the right */}
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: 16,
                                    width: '100%',
                                }}>
                                <TitleWrapper>
                                    <Title className="small">
                                        Meeting URLs
                                    </Title>
                                    <Desc>
                                        {SUPPORTED_MEETING_PLATFORMS_LABEL}
                                    </Desc>
                                </TitleWrapper>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setMeetingUrls?.([
                                            ...(meetingUrls ?? []),
                                            '',
                                        ])
                                    }
                                    style={{
                                        padding: '6px 16px',
                                        borderRadius: 6,
                                        border: '1px solid #ccc',
                                        background: '#fff',
                                        cursor: 'pointer',
                                        fontSize: 14,
                                        flexShrink: 0,
                                    }}>
                                    + Add URL
                                </button>
                            </div>
                            {/* URL inputs render below the header when there are any */}
                            {(meetingUrls ?? []).length > 0 && (
                                <div
                                    style={{
                                        width: '100%',
                                        marginTop: 12,
                                    }}>
                                    {(meetingUrls ?? []).map((url, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                display: 'flex',
                                                gap: 8,
                                                marginBottom: 8,
                                                alignItems: 'center',
                                            }}>
                                            <input
                                                type="url"
                                                value={url}
                                                placeholder="https://calendly.com/..."
                                                onChange={(e) => {
                                                    const updated = [
                                                        ...(meetingUrls ?? []),
                                                    ];
                                                    updated[index] =
                                                        e.target.value;
                                                    setMeetingUrls?.(updated);
                                                }}
                                                style={{
                                                    flex: 1,
                                                    padding: '8px 12px',
                                                    borderRadius: 6,
                                                    border:
                                                        url.trim() &&
                                                        !isValidMeetingUrl(
                                                            url.trim(),
                                                        )
                                                            ? '1px solid #f44336'
                                                            : '1px solid #ccc',
                                                    fontSize: 14,
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setMeetingUrls?.(
                                                        (
                                                            meetingUrls ?? []
                                                        ).filter(
                                                            (_, i) =>
                                                                i !== index,
                                                        ),
                                                    )
                                                }
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: 6,
                                                    border: '1px solid #ccc',
                                                    background: '#fff',
                                                    cursor: 'pointer',
                                                    fontSize: 16,
                                                }}>
                                                −
                                            </button>
                                        </div>
                                    ))}
                                    {(meetingUrls ?? []).some(
                                        (u) =>
                                            u.trim() &&
                                            !isValidMeetingUrl(u.trim()),
                                    ) && (
                                        <p
                                            style={{
                                                color: '#f44336',
                                                fontSize: 12,
                                                marginTop: 4,
                                                marginBottom: 0,
                                            }}>
                                            Invalid URL. Only HTTPS links from
                                            supported platforms are allowed.
                                        </p>
                                    )}
                                    {showMeetingError && (
                                        <p
                                            style={{
                                                color: '#f44336',
                                                fontSize: 12,
                                                marginTop: 4,
                                                marginBottom: 0,
                                            }}>
                                            Add at least one valid meeting link,
                                            or switch meeting scheduling off.
                                        </p>
                                    )}
                                </div>
                            )}
                        </ScanSettingWrapper>
                    )}
                </ScanSettingSection>
            </ForthStepWrapper>
            <ScanSetupFooterWrapper>
                <ButtonWrapper>
                    <PrevButton>
                        <AppButton
                            color={'gray'}
                            onClick={() =>
                                goToPrevStep(
                                    isScanSetupWizard && !newAssessmentSection
                                        ? 'SECOND'
                                        : 'THIRD',
                                )
                            }>
                            Previous
                        </AppButton>
                    </PrevButton>
                    <AppButton onClick={goToNextStep}>Next</AppButton>
                </ButtonWrapper>
            </ScanSetupFooterWrapper>
        </>
    );
};

export default MotiviewStep;
